import { useCallback, useEffect, useState } from 'react';
import { Mail, ShieldCheck, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Alert } from '../components/Alert';
import { Loader } from '../components/Loader';
import { InvitationPreview } from '../types';

/**
 * Where an invitation link lands (`/invite/<token>`).
 *
 * The recipient is usually signed out and often has no account at all, so this
 * page renders before the login gate. It shows who is inviting them, then either
 * creates their account (the Nexus sign-up form: CYRN Auth has registration
 * disabled) or signs them in, and both come back here to accept.
 */
const PENDING_KEY = 'nexus.pendingInvitation';

interface Props {
  token: string;
  onDone: (path: string) => void;
}

export function InvitationPage({ token, onDone }: Props) {
  const { session, refreshSession } = useAuth();
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Consume the parked token as soon as this page takes over. Leaving it set
  // would trap the user here forever on an expired or already-used link, with
  // no way to reach the rest of the app. `signInAndAccept` re-parks it if a
  // sign-in round trip is actually needed.
  useEffect(() => {
    sessionStorage.removeItem(PENDING_KEY);
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.invitations
      .preview(token)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'This invitation link is not valid or has expired.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = useCallback(async () => {
    setAccepting(true);
    setError(null);
    try {
      await api.invitations.accept(token);
      // The new membership changes what the session may see, so it has to be
      // re-read before navigating into the org.
      await refreshSession();
      onDone('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not accept this invitation.');
    } finally {
      setAccepting(false);
    }
  }, [token, refreshSession, onDone]);

  // Both round trips come back to this page, signed in, to accept.
  const here = `/invite/${encodeURIComponent(token)}`;
  const createAccount = () => {
    const params = new URLSearchParams({ return_to: here });
    if (preview?.email) params.set('email', preview.email);
    window.location.href = `/signup?${params.toString()}`;
  };
  const signIn = () => {
    window.location.href = api.auth.loginReturnUrl(here, undefined, preview?.email || undefined);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-canvas)' }}>
        <Loader size={36} label="Checking invitation…" />
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg-canvas)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-6)',
      }}
    >
      <Card padding="var(--space-6)" style={{ maxWidth: 460, width: '100%' }}>
        {error || !preview ? (
          <>
            <Alert tone="danger" title="Invitation unavailable">
              {error || 'This invitation link is not valid or has expired.'}
            </Alert>
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Button variant="secondary" onClick={() => onDone('/')}>
                Go to CYRN Nexus
              </Button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Mail size={22} color="var(--accent)" />
              <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--fw-bold)' }}>
                You have been invited
              </h1>
            </div>

            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--text-primary)' }}>{preview.organisation_name}</strong>{' '}
              has invited <strong style={{ color: 'var(--text-primary)' }}>{preview.email}</strong>{' '}
              to join as <Badge tone="accent">{preview.role.toUpperCase()}</Badge>
            </p>

            <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', lineHeight: 1.6 }}>
              Expires {new Date(preview.expires_at).toLocaleDateString()}. Membership is granted to
              the account you {session ? 'are signed in with' : 'sign in with'}, which does not have to
              use the invited email address.
            </p>

            {session ? (
              <Button
                variant="primary"
                onClick={accept}
                disabled={accepting}
                icon={<ShieldCheck size={14} />}
              >
                {accepting ? 'Joining…' : `Join ${preview.organisation_name}`}
              </Button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                <Button variant="primary" onClick={createAccount} icon={<UserPlus size={14} />}>
                  Create my CYRN account
                </Button>
                <Button variant="secondary" onClick={signIn} icon={<ShieldCheck size={14} />}>
                  I already have a CYRN account
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

export { PENDING_KEY as PENDING_INVITATION_KEY };
